import React, { useState, useEffect, useRef } from 'react';
import './FloatingEmojis.css';

/**
 * A single physics-based floating emoji that bounces off screen edges.
 */
const FloatingEmoji = ({ emoji, index }) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [velocity, setVelocity] = useState({ x: 0, y: 0 });
  const animationRef = useRef();

  useEffect(() => {
    const startX = Math.random() * (window.innerWidth - 60);
    const startY = Math.random() * (window.innerHeight - 60);
    setPosition({ x: startX, y: startY });

    const angle = Math.random() * Math.PI * 2;
    const speed = 0.4 + Math.random() * 0.5;
    setVelocity({
      x: Math.cos(angle) * speed,
      y: Math.sin(angle) * speed
    });
  }, [index]);

  useEffect(() => {
    const animate = () => {
      setPosition(prev => {
        let newX = prev.x + velocity.x;
        let newY = prev.y + velocity.y;

        if (newX <= 0 || newX >= window.innerWidth - 60) {
          setVelocity(v => ({ ...v, x: -v.x * 0.95 }));
          newX = Math.max(0, Math.min(window.innerWidth - 60, newX));
        }
        if (newY <= 0 || newY >= window.innerHeight - 60) {
          setVelocity(v => ({ ...v, y: -v.y * 0.95 }));
          newY = Math.max(0, Math.min(window.innerHeight - 60, newY));
        }

        return { x: newX, y: newY };
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationRef.current);
  }, [velocity]);

  return (
    <div
      className="floating-emoji"
      style={{
        left: position.x,
        top: position.y,
        animationDelay: `${index * 0.3}s`,
      }}
    >
      {emoji}
    </div>
  );
};

/**
 * Renders a layer of physics-based floating emojis.
 * Place inside any position:relative/absolute/fixed container.
 *
 * @param {string}  emoji - The emoji character to float
 * @param {number}  [count=8] - How many emoji instances to render
 */
const FloatingEmojiLayer = ({ emoji, count = 8 }) => (
  <div className="floating-emoji-layer">
    {[...Array(count)].map((_, i) => (
      <FloatingEmoji key={i} emoji={emoji} index={i} />
    ))}
  </div>
);

export { FloatingEmoji, FloatingEmojiLayer };
export default FloatingEmojiLayer;
